
import { PlayerObject } from "@models/UpsertableBase";
import {
  Feature,
  IStringHash,
  SmartHash,
} from "@models";
import { 
  CharacterClass, 
  CharacterBackground,
  CharacterASIBaseFeature,
  CharacterRace,
  CharacterResource, 
  CharacterSpecialFeature, 
  CharacterItem,
  CharacterFeat,
  CharacterAbility, 
  CharacterSpell, 
  CharacterSlot,
  CharacterSense,
  CharacterGift,
  CharacterAction,
  CharacterArmor,
  CharacterAmmunition,
  CharacterBasicItem,
  CharacterContainer,
  CharacterWeapon,
  CharacterCustomItem,
  CharacterFeatureBase,
  CharacterOptions,
  AbilityScores,
  Money, 
  HitDice, 
  Armor,
  DamageMultiplierSimple,
  Bonus,
  BasicCreatureInstance,
  SummonInstance,
  Reroll,
  Campaign, 
  Modifier,
  CharacterFeature,
  CharacterEldritchInvocation,
  CharacterFightingStyle,
  CharacterPactBoon,
  // SpellModifier,
  CharacterProficiency,
  CharacterAdvantage,
  RollPlus,
  CharacterConditionFeature,
  CharacterFeatureSource,
  WeaponKeywordUpgradeFeature,
  MinimumRoll,
  SummonUpgradeFeature,
  ActionType,
  ActionTypeUpgrade,
  CharacterOrg,
  CharacterSheetInterface,
  TransformInstance,
  // AdaptationInstance,
  CharacterWeaponMastery,
  SkillAbilityOverrideFeature,
  UpgradableNumber,
  CharacterExtraEffects,
  CharacterDeck,
  CharacterGem,
  CharacterRecipe,
  CharacterRecipeComponent,
  AlternateConditionFeature,
  WeaponKeywordUpgradeFeature2,
  Creature,
  CharacterWeaponMasteryMaster,
  SizeOptions,
  CharacterResourceHolder,
  CharacterSubscriptionHolder,
  GreaterAdvantage,
  CharacterBook,
  CreatureHP,
  CharacterStandardFeatures,
  // FeatureAncestry,
  CharacterAbilityModifier,
  StoredSpell,
  CharacterReceivable,
  CharacterBastionFacility,
  CharacterCondition,
  ComplexCreatureInstance,
  CampaignChar,
  // SpellsFromList,
  // SpellsFromListWithCosts,
} from "@dndModels";
import { CharacterKnowledge } from "@dndModels/character_feature_source/CharacterKnowledge";
import { AttackType, CreatureType, DamageType, DataType, ModType, SizeType } from "@dndModels/Types";
import CommonUtilities from "@utilities/common_utilities";
import { SIZE_MAP } from "@dndModels/Constants";
import { ColorsType } from "../../../../DandDPalette";

export interface TurnResourceNumber {
  used: number;
  total: number;
}

export interface TurnResources {
  A: TurnResourceNumber, BA: TurnResourceNumber, RA: TurnResourceNumber, Attack: TurnResourceNumber, LAttack: TurnResourceNumber
}

const common_util = CommonUtilities.getInstance();

export class Character extends PlayerObject implements CharacterSheetInterface {
  subscription_holder: CharacterSubscriptionHolder = new CharacterSubscriptionHolder();
  archived_data_holder: SmartHash = {};
  
  shared_id = "";

  last_saved = 0;
  cookie_saved = 0; 
  last_changed = 0;
  dont_auto_save = false;

  c_char?: CampaignChar;

  changed() {
    this.last_changed = common_util.get_time_number();
    this.subscription_holder.characterChanged();
  }
  get changed_since_saved() {
    return this.last_changed > this.last_saved || this.cookie_saved > this.last_saved;
  }
  get changed_since_cookie_saved() {
    return this.last_changed > this.cookie_saved;
  }
  get cookie_outdated() {
    return this.last_saved > this.cookie_saved;
  }

  turn_resources: TurnResources = {
    A: { used: 0, total: 1 }, 
    BA: { used: 0, total: 1 }, 
    RA: { used: 0, total: 1 }, 
    Attack: { used: 0, total: 1 }, 
    LAttack: { used: 0, total: 1 }
  };

  knowledge: CharacterKnowledge[] = [];
  // From CharacterSheetInterface
  // adaptations: AdaptationInstance[] = [];
  image_url = "";
  bg_image_url = "";

  size: SizeType = 0; 
  base_size: SizeType = 0;
  size_modifier = 0;
  overriding_size: SizeType = 0;
  size_option_features: SizeOptions[] = [];
  size_options: SizeType[] = [];
  size_bonus_for_carrying = 0;
  carrying_capacity_mult = 1;
  max_carrying_capacity = 0;

  get use_size() {
    if (this.overriding_size === 0) {
      const use_size = this.base_size + this.size_modifier;
      if (SIZE_MAP[use_size]) {
        return use_size as SizeType;
      } else {
        return this.base_size;
      }
    } else {
      return this.overriding_size;
    }
  }
  
  // This is the real ability score, calculated using all of the others
  current_ability_scores: AbilityScores = new AbilityScores();
  // These limit the effectiveness of any modifiers.  
  // Almost always will be 20 for everything, 
  // but some features change them.
  max_ability_scores: AbilityScores = new AbilityScores(20);
  // This holds all the modifications from ASIs, etc. 
  // so it can easily be displayed
  bonus_ability_score_modifiers: AbilityScores = new AbilityScores(0);
  // This holds the overridden ability scores from things 
  // like Belt of Giant Strength, or Amulet of Health.
  override_ability_scores: AbilityScores = new AbilityScores(null);

  advantage = 0;
  greater_advantages: GreaterAdvantage[] = [];
  inspiration = false;

  resource_holder: CharacterResourceHolder = new CharacterResourceHolder();
  resource_holder_is_good = true;
  
  spells: CharacterSpell[] = [];
  ritual_only: CharacterSpell[] = [];
  abilities: CharacterAbility[] = [];
  spell_as_abilities: CharacterAbility[] = [];

  // get spells_from_lists() {
  //   return this.spell_as_abilities.filter(o => 
  //     o.the_ability instanceof SpellsFromList
  //   );
  // }
  
  // get spells_from_lists_with_costs() {
  //   return this.spell_as_abilities.filter(o => 
  //     o.the_ability instanceof SpellsFromListWithCosts
  //   );
  // }
  
  get resources() {
    return this.resource_holder.resources;
  }
  get slots() {
    return this.resource_holder.slots;
  }
  get max_slot_level() {
    return Math.max(...this.slots.map(o => o.level), 0);
  }
  get infusions_by_level() {
    return this.resource_holder.infusions;
  }
  get infusions() {
    return this.infusions_by_level.flatMap(o => o.infusions);
  }
  get saved_allies() {
    return this.resource_holder.saved_allies;
  }
  
  get use_slots(): CharacterSlot[] {
    const resource_slots = this.resources.filter(o => o.is_slot).map(o => o.slot);
    return [...this.slots,...resource_slots];
  }

  removeSlots(type_id: string) {
    this.resource_holder.slots = this.resource_holder.slots.filter(o => !o.type_id.includes(type_id));
  }
  
  languages_known: string[] = [];
  
  proficiencies: CharacterProficiency[] = [];
  jack_of_all_trades = false;
  
  table_modifiers: Modifier[] = [];
  spell_modifiers: CharacterAbilityModifier[] = [];
  passive_bonuses: {
    perception: number,
    insight: number,
    investigation: number
  } = {
    perception: 0,
    insight: 0,
    investigation: 0
  };
  range_bonuses: RangeBonus[] = [];

  rerolls: Reroll[] = [];
  minimum_rolls: MinimumRoll[] = [];
  
  speed: {
    walk: number,
    swim: number,
    climb: number,
    fly: number,
    burrow: number
  } = {
    walk: 0,
    swim: 0,
    climb: 0,
    fly: 0,
    burrow: 0
  };
  
  attack_bonuses: Bonus[] = [];
  damage_bonuses: Bonus[] = [];
  check_bonuses: Bonus[] = [];
  initiative_modifier = 0;
  initiative_bonuses: Bonus[] = [];
  base_armor_class = 10;
  armor_class = 10;
  max_ac_dex_bonus_for_med_armor = 2;
  saved_armor_class = 10;
  
  get use_ac() {
    if (this.classes.length > 0) {
      return this.armor_class;
    } else {
      return this.saved_armor_class;
    }
  }

  ignore_stealth_disadvantage_for_armor_type_ids: string[] = [];
  
  ability_checks: { 
    STR: CharacterRoll, 
    DEX: CharacterRoll, 
    CON: CharacterRoll, 
    INT: CharacterRoll, 
    WIS: CharacterRoll, 
    CHA: CharacterRoll 
  } = { 
    STR: new CharacterRoll(), 
    DEX: new CharacterRoll(), 
    CON: new CharacterRoll(), 
    INT: new CharacterRoll(), 
    WIS: new CharacterRoll(), 
    CHA: new CharacterRoll() 
  };
  initiative_check: CharacterRoll = new CharacterRoll();
  saving_throw_bonuses: Bonus[] = [];
  saving_throw_profs: { [ability_score: string]: number } = {};
  saving_throws: { 
    [ability_score: string]: CharacterRoll
  } = { 
    STR: new CharacterRoll(), 
    DEX: new CharacterRoll(), 
    CON: new CharacterRoll(), 
    INT: new CharacterRoll(), 
    WIS: new CharacterRoll(), 
    CHA: new CharacterRoll()
  };
  
  AC_customization: Customization = { AC: {} };
  speed_customizations: Customization = {};
  sense_customizations: Customization = {};
  display_speed: "walk" | "swim" | "climb" | "fly" | "burrow" = "walk";
  saving_throw_customizations: Customization = {};
  skill_customizations: Customization = {};
  max_ability_score_customizations: Customization = {};
  ability_score_customizations: Customization = {};
  non_skill_prof_customizations: { [prof_type: string]: { id: string, proficiency: boolean | null, notes: string }[] } = {};
  weapon_mastery_customizations: CharacterWeaponMastery[] = [];
  weapon_mastery_overrides: CharacterWeaponMastery[] = [];
  attunement_customization: UpgradableNumber | null = null;
  
  skill_checks: { skill_id: string, skill_check: CharacterRoll }[] = [];

  senses: CharacterSense[] = [];
  
  advantages: CharacterAdvantage[] = [];
  damage_multipliers: DamageMultiplierSimple[] = [];
  extra_attacks = 0;

  getDTsForMultiplier(multiplier: number) {
    let dts = this.getDTsForMultiplierSimple(multiplier);
    if (multiplier === 0) {
      dts.push(...this.getUpgradedDTs());
    } else if (multiplier === 0.5) {
      const immune = this.getDTsForMultiplier(0);
      dts = dts.filter(dt => !immune.includes(dt));
    } 
    return common_util.unique_array(dts);
  }

  getDTsForMultiplierSimple(multiplier: number) {
    return common_util.unique_array(this.damage_multipliers.filter(o => o.multiplier === multiplier).map(o => o.damage_type));
  }

  getUpgradedDTs() {
    const upgraded: DamageType[] = [];
    const res_dms = this.damage_multipliers.filter(o => o.multiplier === 0.5 && o.multiplier_improvement);
    for (const dm of res_dms) {
      // Look for another dm for the same type which is also resistance.
      const other_dm = this.damage_multipliers.find(o => dm.damage_type === dm.damage_type && o.true_id !== dm.true_id && o.multiplier === 0.5);
      if (other_dm && dm.damage_type !== "" && !upgraded.includes(dm.damage_type)) {
        upgraded.push(dm.damage_type);
      }
    }
    return upgraded;
  }

  unarmed_strike_scores: string[] = [];
  unarmed_strike_bonus_action = false;
  unarmed_strike_damage_types: DamageType[] = [];
  unarmed_strike_size = 0;
  unarmed_strike_count = 0;
  unarmed_strike_add_attack_to_dmg = false;
  unarmed_strike_customizations: { 
    name: string, 
    hidden: boolean,
    hide_in_pdf: boolean,
    bonus_action: string, 
    add_attack_to_dmg: string,
    size?: number, 
    count?: number, 
    scores?: string[], 
    damage_types?: DamageType[], 
  } = {
    name: "Unarmed Strike",
    hidden: false,
    hide_in_pdf: false,
    bonus_action: "Don't Override",
    add_attack_to_dmg: "Don't Override"
  };

  show_extra_conditions: string[] = [];
  
  actions: CharacterAction[] = [];

  data_type: DataType = "character";
  connected = false;
  old_pieces = false;
  owner_id = "";
  get active() {
    if (this.c_char) {
      return this.c_char.active;
    }
    return true;
  }
  // is_favorite: boolean = false;
  // is_archived: boolean = false;

  character_options: CharacterOptions = new CharacterOptions();
  level_0_feature_ids: string[] = [];
  bonus_skill_profs_ids: string[] = [];

  old_campaign_id = "";
  get campaign() {
    if (this.c_char) {
      return this.c_char.campaign;
    }
    return undefined;
  }

  sidekick = false;
  sidekick_creature_id = "";
  sidekick_creature: Creature | undefined = undefined;
  sidekick_ci: BasicCreatureInstance | undefined = undefined;
  race: CharacterRace = new CharacterRace();
  gifts: CharacterGift[] = [];
  classes: CharacterClass[] = [];
  background: CharacterBackground = new CharacterBackground();
  standard_features: CharacterStandardFeatures = new CharacterStandardFeatures();
  bastion_facilities: CharacterBastionFacility[] = [];

  orgs: CharacterOrg[] = [];

  ability_score_gen_method: "Point Buy" | "Standard Array" | "Manual/Rolled" = "Point Buy";
  // These are what is set in the main part using the gen method above
  base_ability_scores: AbilityScores = new AbilityScores();
  
  organizations = "";
  allies = "";
  enemies = "";
  backstory = "";
  other_notes = "";

  characteristics: { [key: string]: string } = {
    faith: "",
    alignment: "Neutral",
    lifestyle: "Comfortable (2 GP)",
    hair: "",
    skin: "",
    eyes: "",
    height: "",
    weight: "",
    age: "",
    gender: ""
  };
  appearance = "";

  items: CharacterItem[] = [];
  equipped_items: CharacterItem[] = [];
  attuned_items: CharacterItem[] = [];
  containers: CharacterContainer[] = [];
  recipes_known: CharacterRecipe[] = [];
  recipe_components: CharacterRecipeComponent[] = [];

  get ammunitions() {
    return this.items.filter(o => 
      o instanceof CharacterAmmunition && 
      o.count > 0) as CharacterAmmunition[];
  }

  max_attuned_items = 3;
  money: Money = new Money();
  other_possessions = "";
  default_view = "Main";
  bar3_mode = "Resources";

  // These are feats granted by the DM, not gained through ASI or race choices
  extra_feats: CharacterFeat[] = [];
  // These are damage multipliers added by spell effects, etc
  extra_damage_multipliers: DamageMultiplierSimple[] = [];
  
  favorite_conditions: string[] = [];
  // condition_hash: { [id: string]: { level: number, source_true_id: string } } = {};
  condition_immunities: string[] = [];
  conditions: CharacterCondition[] = [];

  get condition_hash() {
    const hash:{ [id: string]: { level: number, source_true_id: string } } = {};
    for (const cc of this.conditions) {
      hash[cc.source_id] = { level: cc.level, source_true_id: cc.condition_source_true_id };
    }
    return hash;
  }

  // death_save_fails: number = 0;
  // death_save_successes: number = 0;

  characteristic_feature_source_names: string[] = [];
  
  concentrating_on: CharacterSpell | StoredSpell | CharacterAbility | null = null;

  minions: (BasicCreatureInstance | SummonInstance | TransformInstance | ComplexCreatureInstance)[] = [];
  transform: TransformInstance | null = null;
  use_transform = false;

  get char_interface(): CharacterSheetInterface {
    if (this.use_transform && this.transform) {
      return this.transform;
    }
    return this;
  }
  // This is for storing customizations that were done on currently inactive transforms
  // Not currently implemented
  // transforms: TransformInstance[] = []; 

  optional_feature_base_ids: string[] = [];

  last_campaign_update = 0;

  // Partially Calculated

  // Calculated attributes
  conditions_from_features: CharacterConditionFeature[] = [];
  feats: CharacterFeat[] = [];
  // feat_ids: string[] = [];
  eldritch_invocations: CharacterEldritchInvocation[] = [];
  // eldritch_invocation_ids: string[] = [];
  fighting_styles: CharacterFightingStyle[] = [];
  // fighting_style_ids: string[] = [];
  pact_boon: CharacterPactBoon | null = null;
  summon_upgrade_features: CharacterFeature[] = [];
  skill_ability_overrides: SkillAbilityOverrideFeature[] = [];
  // pact_boon_id: string = "";

  special_features: CharacterSpecialFeature[] = [];

  char_feature_bases: CharacterFeatureBase[] = [];

  features: CharacterFeature[] = [];
  resource_features: CharacterFeature[] = [];
  ability_features: CharacterFeature[] = [];
  characteristic_features: CharacterFeature[] = [];
  asi_features: CharacterFeature[] = [];
  ability_score_features: { cf: CharacterFeature, casibf: CharacterASIBaseFeature }[] = [];
  
  language_features: CharacterFeature[] = [];
  
  proficiency_features: CharacterFeature[] = [];

  modifier_features: CharacterFeature[] = [];
  spell_modifier_features: CharacterFeature[] = [];
  disable_action_features: CharacterFeature[] = [];
  attack_upgrades: CharacterFeature[] = [];
  weapon_damage_upgrades: CharacterFeature[] = [];
  weapon_mastery_features: CharacterFeature[] = [];
  cwms: CharacterWeaponMastery[] = [];
  cwmms: CharacterWeaponMasteryMaster[] = [];
  extra_effects: CharacterExtraEffects[] = [];
  extra_effects_features: CharacterFeature[] = [];
  // bonus_action_attacks: CharacterFeature[] = [];
  light_attack_add_damage_mods: CharacterFeature[] = [];

  source_features: CharacterFeature[] = [];

  sense_features: CharacterFeature[] = [];

  start_equipment_features: CharacterFeature[] = [];
  misc_features: CharacterFeature[] = [];
  spellcasting_features: CharacterFeature[] = [];

  alternate_condition_features: AlternateConditionFeature[] = [];
  limit_spoof_features: CharacterFeature[] = [];
  rest_alteration_features: CharacterFeature[] = [];

  transform_upgrades: CharacterFeature[] = [];

  knowledge_features: CharacterFeature[] = [];
  learnable_features: CharacterFeature[] = [];
  spell_damage_options: CharacterFeature[] = [];

  full_actions: ActionType[] = [];
  bonus_actions: ActionType[] = [];
  reactions: ActionType[] = [];
  action_type_upgrades: ActionTypeUpgrade[] = [];

  merged_special_feature_types: string[][] = [];

  formula_values: { [key: string]: number } = {};
  encumbrance_level = 0;
  special_feat_accesses: string[] = [];

  recalc_time = -1;

  saved_race_and_class_string = "";
  saved_race_string = "";
  saved_race_ids: string[] = [];
  saved_subrace_ids: string[] = [];
  saved_class_info: { class_id: string, subclass_id: string, level: number, class_name: string }[] = [];
  // saved_subclass_ids: string[] = [];
  // saved_campaign_id: string = "";
  saved_extras_string = "";

  get class_info() { 
    if (this.classes.length > 0) {
      return this.classes.map(o => {
        return {
          class_id: o.source_id, 
          subclass_id: o.subclass_id, 
          level: o.level, 
          class_name: o.get_use_name() 
        };
      });
    } else {
      return this.saved_class_info;
    }
  }

  show_extra_effects: string[] = [];

  // For Creature Transforms
  spellcasting_ability = "";
  spell_dc: UpgradableNumber = new UpgradableNumber();
  spell_attack: UpgradableNumber = new UpgradableNumber();
  non_magic_attack: UpgradableNumber = new UpgradableNumber();
  magic_dmg_bonus: UpgradableNumber = new UpgradableNumber();
  non_magic_dmg_bonus: UpgradableNumber = new UpgradableNumber();
  string_hash: { [key: string]: string; } = {};
  upgradable_hash: { [key: string]: UpgradableNumber; } = {};
    
  colors_for: {
    [for_id: string]: ColorsType
  } = {};

  receivables: CharacterReceivable[] = [];

  dragonmarked_source_id = "";
  wild_talent_source_id = "";

  get character_level(): number {
    if (this.classes.length > 0) {
      return this.classes.reduce((accumulator: number, char_class: CharacterClass) => {
        return accumulator + char_class.level;
      }, 0);
    } else {
      return this.saved_class_info.reduce((accumulator: number, obj: { class_id: string, subclass_id: string, level: number, class_name: string }) => {
        return accumulator + obj.level;
      }, 0);
    }
  }

  get armor_worn(): boolean {
    const armor = this.equipped_items.find(o => o.base_item instanceof Armor && o.base_item.armor_type_name !== "Shield");
    if (armor) {
      return true;
    }
    return false;
  }
  get shield_carried(): boolean {
    const shield = this.equipped_items.find(o => o.base_item instanceof Armor && o.base_item.armor_type_name === "Shield");
    if (shield) {
      return true;
    }
    return false;
  }

  get proficiency_modifier(): number {
    return common_util.proficiencyBonus(this.character_level);
  }

  override_creature_type?: CreatureType;
  get creature_type() {
    if (this.override_creature_type) {
      return this.override_creature_type;
    }
    if (this.sidekick && this.sidekick_creature) {
      return this.sidekick_creature.creature_type;
    }
    return this.race.creature_type;
  }

  set creature_type(value: CreatureType) {
    this.override_creature_type = value;
  }

  get weapon_masteries(): CharacterWeaponMastery[] {
    return [...this.cwms,...this.weapon_mastery_customizations,...this.weapon_mastery_overrides];
  }

  get standard_weapon_masteries() {
    return this.cwms.filter(o => o.weapon_limits.some(o2 => o2.isEmpty(["item_ids"])));
  }

  get standard_weapon_mastery_weapons() {
    const ids: string[] = [];
    for (const cwm of this.cwms) {
      const limit = cwm.weapon_limits.find(o => o.isEmpty(["item_ids"]));
      if (limit) {
        ids.push(...limit.item_ids);
      }
    }
    return ids;
  }

  get race_ids(): string[] {
    return this.race.search_ids.length > 0 ? this.race.search_ids : this.saved_race_ids;
  }

  get subrace_ids(): string[] {
    if (this.race.char_subrace) {
      return this.race.char_subrace.search_ids.length > 0 ? this.race.char_subrace.search_ids : this.saved_subrace_ids;
    }
    return this.saved_subrace_ids;
  }

  get class_ids(): string[] {
    const class_ids = this.classes.flatMap(o => o.search_ids);
    return class_ids.length > 0 ? class_ids : this.saved_class_info.map(o => o.class_id);
  }

  get subclass_ids(): string[] {
    const subclass_ids = this.classes.flatMap(o => o.char_subclass?.search_ids??[]);
    return subclass_ids.length > 0 ? subclass_ids : this.saved_class_info.map(o => o.subclass_id);
  }

  notes = "";

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      // if (this.name.includes("Marius")) {
      //   debugger;
      // }
      this.shared_id = obj.shared_id ? obj.shared_id : "";
      this.last_saved = obj.last_saved ? obj.last_saved : 0;
      this.cookie_saved = obj.cookie_saved ? obj.cookie_saved : 0;
      this.turn_resources = obj.turn_resources ? obj.turn_resources : {
        A: { used: 0, total: 1 }, 
        BA: { used: 0, total: 1 }, 
        RA: { used: 0, total: 1 }, 
        Attack: { used: 0, total: 0 }, 
        LAttack: { used: 0, total: 0 }
      };
      this.owner_id = obj.owner_id ? obj.owner_id : "";
      this.sidekick = obj.sidekick ? obj.sidekick : false;
      this.sidekick_creature_id = obj.sidekick_creature_id ? obj.sidekick_creature_id : "";
      this.old_campaign_id = obj.campaign_id ? obj.campaign_id : "";
      // this.active = obj.active !== undefined ? obj.active : true;
      if (obj.character_options) {
        this.character_options = new CharacterOptions(obj.character_options);
      }
      this.knowledge = obj.knowledge ? obj.knowledge : [];
      // this.adaptations = [];
      this.bonus_skill_profs_ids = obj.bonus_skill_profs_ids ? [...obj.bonus_skill_profs_ids] : [];
      this.optional_feature_base_ids = obj.optional_feature_base_ids ? [...obj.optional_feature_base_ids] : [];
      this.image_url = obj.image_url ? obj.image_url : "";
      this.bg_image_url = obj.bg_image_url ? obj.bg_image_url : "";
      this.is_favorite = obj.is_favorite ? obj.is_favorite : false;
      this.is_archived = obj.is_archived ? obj.is_archived : false;
      this.default_view = obj.default_view ? obj.default_view : "Main";
      if (!["Main","Actions","Spells","Inventory","Features & Traits","Description","Notes","Options"].includes(this.default_view)) {
        this.default_view = "Main";
      }
      this.bar3_mode = obj.bar3_mode ? obj.bar3_mode : "Resources";
      this.level_0_feature_ids = obj.level_0_feature_ids ? [...obj.level_0_feature_ids] : [];
      
      this.organizations = obj.organizations ? obj.organizations : "";
      this.allies = obj.allies ? obj.allies : "";
      this.enemies = obj.enemies ? obj.enemies : "";
      this.backstory = obj.backstory ? obj.backstory : "";
      this.other_notes = obj.other_notes ? obj.other_notes : "";
      this.characteristics = obj.characteristics ? obj.characteristics : {
        faith: "",
        alignment: "Neutral",
        lifestyle: "Comfortable (2 GP)",
        hair: "",
        skin: "",
        eyes: "",
        height: "",
        weight: "",
        age: "",
        gender: ""
      };
      if (!this.characteristics.faith) { this.characteristics.faith = ""; }
      if (!this.characteristics.alignment) { this.characteristics.alignment = "Neutral"; }
      if (!this.characteristics.lifestyle) { this.characteristics.lifestyle = "Comfortable (2 GP)"; }
      if (!this.characteristics.hair) { this.characteristics.hair = ""; }
      if (!this.characteristics.skin) { this.characteristics.skin = ""; }
      if (!this.characteristics.eyes) { this.characteristics.eyes = ""; }
      if (!this.characteristics.height) { this.characteristics.height = ""; }
      if (!this.characteristics.weight) { this.characteristics.weight = ""; }
      if (!this.characteristics.age) { this.characteristics.age = ""; }
      if (!this.characteristics.gender) { this.characteristics.gender = ""; }
      this.appearance = obj.appearance ? obj.appearance : "";
      this.inspiration = obj.inspiration ? true : false;
      this.base_size = obj.base_size ? obj.base_size : 0;
      this.size_modifier = obj.size_modifier ? obj.size_modifier : 0;
      this.overriding_size = obj.overriding_size ? obj.overriding_size : 0;
      this.speed_customizations = obj.speed_customizations ? obj.speed_customizations : {
        walk: { value: -1, notes: "" },
        swim: { value: -1, notes: "" },
        climb: { value: -1, notes: "" },
        fly: { value: -1, notes: "" },
        burrow: { value: -1, notes: "" }
      };
      this.unarmed_strike_customizations = obj.unarmed_strike_customizations ? obj.unarmed_strike_customizations : {
        name: "Unarmed Strike",
        hide_in_pdf: false,
        bonus_action: "Don't Override",
        add_attack_to_dmg: "Don't Override"
      };
      this.attunement_customization = obj.attunement_customization ? new UpgradableNumber(obj.attunement_customization) : null;
      this.non_skill_prof_customizations = obj.non_skill_prof_customizations ? obj.non_skill_prof_customizations : {};
      this.weapon_mastery_customizations = obj.weapon_mastery_customizations ? obj.weapon_mastery_customizations.map((o: any) => { return new CharacterWeaponMastery(o); }) : [];
      this.saving_throw_customizations = obj.saving_throw_customizations ? obj.saving_throw_customizations : {};
      this.skill_customizations = obj.skill_customizations ? obj.skill_customizations : {};
      this.max_ability_score_customizations = obj.max_ability_score_customizations ? obj.max_ability_score_customizations : {};
      this.ability_score_customizations = obj.ability_score_customizations ? obj.ability_score_customizations : {};
      
      this.AC_customization = obj.AC_customization ? obj.AC_customization : { AC: {} };
      this.display_speed = obj.display_speed ? obj.display_speed : "walk";

      this.last_campaign_update = obj.last_campaign_update ? obj.last_campaign_update : 0;
      this.colors_for = obj.colors_for ? obj.colors_for : {};
      
      this.base_ability_scores = new AbilityScores();
      this.items = [];
      this.equipped_items = [];
      this.attuned_items = [];
      this.containers = [];
      this.max_attuned_items = 3; // obj.max_attuned_items ? obj.max_attuned_items : 3;
      this.money = new Money();
      this.other_possessions = ""; // obj ? obj.other_possessions : "";
      this.extra_feats = [];
      this.extra_damage_multipliers = [];
      this.favorite_conditions = []; // obj.favorite_conditions ? obj.favorite_conditions : [];
      this.conditions = [];
      // this.condition_hash = {}; // obj.conditions ? obj.conditions : [];
      this.condition_immunities = []; // obj.condition_immunities ? obj.condition_immunities : [];
      this.spells = [];
      // this.special_spells = [];
      this.abilities = [];
      this.spell_as_abilities = [];
      this.concentrating_on = null;
      this.advantage = 0; // obj.advantage ? obj.advantage : null;
      this.connected = false;
      this.minions = [];
      this.transform = null;
      this.use_transform = false;
      // Calculated
      this.ritual_only = [];
      this.current_ability_scores = new AbilityScores();
      this.languages_known = [];
      this.proficiencies = [];
      this.special_features = [];
      this.conditions_from_features = [];
      this.feats = [];
      this.speed = {
        walk: 0,
        swim: 0,
        climb: 0,
        fly: 0,
        burrow: 0
      };
      this.attack_bonuses = [];
      this.damage_bonuses = [];
      this.saving_throw_bonuses = [];
      this.check_bonuses = [];
      this.initiative_modifier = 0;
      this.initiative_bonuses = [];
      // this.armor_class = 0;
      this.saving_throw_profs = {
        STR: 0, DEX: 0, CON: 0, 
        INT: 0, WIS: 0, CHA: 0
      };
      this.max_ability_scores = new AbilityScores();
      // this.armor_worn = false;
      // this.shield_carried = false;
      this.senses = [];
      this.advantages = [];
      this.damage_multipliers = [];
      this.actions = [];
      this.extra_attacks = 0;
      this.unarmed_strike_scores = ["STR"];
      this.unarmed_strike_bonus_action = false;
      this.unarmed_strike_damage_types = ["Bludgeoning"];
      this.unarmed_strike_size = 4;
      this.unarmed_strike_count = 1;
      this.rerolls = [];
      this.table_modifiers = [];

      this.saved_race_and_class_string = obj.saved_race_and_class_string ? (`${obj.saved_race_and_class_string}`).replace("(","").replace(")","") : "";
      this.saved_race_string = obj.saved_race_string ? obj.saved_race_string : "";
      this.saved_race_ids = obj.saved_race_ids ? obj.saved_race_ids : [];
      this.saved_subrace_ids = obj.saved_subrace_ids ? obj.saved_subrace_ids : [];
      this.saved_class_info = obj.saved_class_info ? obj.saved_class_info : [];
      this.saved_armor_class = obj.saved_armor_class ? obj.saved_armor_class : 10;
      // this.saved_class_ids = obj.saved_class_ids ? obj.saved_class_ids : [];
      // this.saved_subclass_ids = obj.saved_subclass_ids ? obj.saved_subclass_ids : [];
      // // this.saved_campaign_id = obj.saved_campaign_id ? obj.saved_campaign_id : "";
      this.saved_extras_string = obj.saved_extras_string ? obj.saved_extras_string : "";

      this.notes = obj.notes ? obj.notes : "";
    }
  }

  get_detail_format() {
    const str = `${this.race_and_class_string} ${this.extras_string}`;
    return str.trim();
  }

  apply_piece(piece_name: string, piece: any) {
    if (!piece.message && piece) {
      // if (piece_name === "classes") {
      //   console.log(piece);
      //   debugger;
      // }
      switch(piece_name) {
        case "main": 
        break;
        case "abilities": 
          // char_id: this._id,
          if (this.character_options.ability_score_gen_method === "Any" || 
            this.character_options.ability_score_gen_method === "Not Manual/Rolled") {
            if (piece.ability_score_gen_method) {
              if (this.character_options.ability_score_gen_method === "Any" || 
                (this.character_options.ability_score_gen_method === "Not Manual/Rolled" && 
                  piece.ability_score_gen_method !== "Manual")) {
                this.ability_score_gen_method = piece.ability_score_gen_method;
              }
            }
          } else {
            this.ability_score_gen_method = this.character_options.ability_score_gen_method;
          }
          this.base_ability_scores = piece ? new AbilityScores(piece.base_ability_scores) : new AbilityScores();
        break;
        case "features":
          // char_id: this._id,
          this.extra_feats = [];
          if (piece && piece.extra_feats) {
            for (let i = 0; i < piece.extra_feats.length; i++) {
              const feat = new CharacterFeat(piece.extra_feats[i]);
              if (feat.source_id === "") {
                return "feat";
              }
              this.extra_feats.push(feat);
            }
          }
          this.extra_damage_multipliers = [];
          if (piece && piece.extra_damage_multipliers) {
            for (let i = 0; i < piece.extra_damage_multipliers.length; i++) {
              const dm = new DamageMultiplierSimple(piece.extra_damage_multipliers[i]);
              this.extra_damage_multipliers.push(dm);
            }
          }
          this.favorite_conditions = piece ? piece.favorite_conditions : [];
          this.conditions = [];
          if (piece && piece.conditions) {
            for (let i = 0; i < piece.conditions.length; i++) {
              const cc = new CharacterCondition(piece.conditions[i]);
              if (cc.source_id === "") {
                return "condition";
              }
              this.conditions.push(cc);
            }
          } else if (piece && piece.condition_hash) {
            const cond_ids = Object.keys(piece.condition_hash);
            for (const cond_id of cond_ids) {
              if (typeof piece.condition_hash[cond_id] === "object") {
                // this.condition_hash[cond_id] = piece.condition_hash[cond_id];
                const cc = new CharacterCondition();
                cc.source_id = cond_id;
                cc.level = piece.condition_hash[cond_id].level;
                cc.condition_source_true_id = piece.condition_hash[cond_id].source_true_id;
                this.conditions.push(cc);
              } else if (typeof piece.condition_hash[cond_id] === "number") {
                // this.condition_hash[cond_id] = { level: piece.condition_hash[cond_id], source_true_id: "" };
                const cc = new CharacterCondition();
                cc.source_id = cond_id;
                cc.level = piece.condition_hash[cond_id];
                this.conditions.push(cc);
              }
            }
          }
          this.condition_immunities = piece ? piece.condition_immunities : [];
          this.advantage = piece ? +piece.advantage : 0;
          this.concentrating_on = null;
          if (piece && piece.concentrating_on) {
            if (piece.concentrating_on.ability_type) {
              this.concentrating_on = new CharacterAbility(piece.concentrating_on);
              const source_id = this.concentrating_on.source_id;
              if (this.concentrating_on.source_type === "Class") {
                const class_finder = this.classes.filter(o => o.source_id === source_id);
                if (class_finder.length === 1) {
                  this.concentrating_on.connectSource(class_finder[0]);
                }
              }
            } else if (piece.concentrating_on.spell_id) {
              this.concentrating_on = new CharacterSpell(piece.concentrating_on);
            }
          }
          this.minions = [];
          if (piece && piece.minions && piece.minions.length > 0) {
            // console.log(piece.minions);
            for (const ci of piece.minions) {
              // debugger;
              if (ci.instance_type === undefined && ci.transform_id && ci.transform_id !== "") {
                this.minions.push(new TransformInstance(ci));
              } else if (ci.instance_type && ci.instance_type === "Summon") {
                const si = new SummonInstance(ci);
                if (si.use_your_pb) {
                  si.your_pb = this.proficiency_modifier;
                }
                this.minions.push(si);
              } else if (ci.instance_type === "ComplexCreatureInstance") {
                console.log(ci);
                // debugger;
                const cci = new ComplexCreatureInstance(ci);
                cci.owner_char = this;
                if (cci.use_your_pb) {
                  cci.your_pb = this.proficiency_modifier;
                }
                this.minions.push(cci);
              } else {
                this.minions.push(new BasicCreatureInstance(ci));
              }
            }
          }
          this.transform = piece && piece.transform && piece.transform.tool_proficiencies === undefined ? new TransformInstance(piece.transform) : null;
          this.use_transform = piece && piece.transform && piece.use_transform ? piece.use_transform : false;
        break;
        case "hp": 
          if (piece.resource_holder) {
            this.resource_holder = new CharacterResourceHolder(piece.resource_holder);
          } else {
            this.resource_holder_is_good = false;
            this.resource_holder = new CharacterResourceHolder();
            this.resource_holder.hp = piece.hp ? new CreatureHP(piece.hp) : new CreatureHP();
            this.resource_holder.hit_dice = [];
            if (piece && piece.hit_dice && piece.hit_dice.length > 0) {
              for (let i = 0; i < piece.hit_dice.length; i++) {
                const char_hit_dice = new HitDice(piece.hit_dice[i]);
                this.resource_holder.hit_dice.push(char_hit_dice);
              }
            }
            // Resources and slots are in spells.  
            // They're getting moved to hp as part of this.
          }
        break;
        case "spells": 
          // char_id: this._id,
          // console.log(piece);
          let get_special_resources = false;
          if (piece && piece.resources && !this.resource_holder_is_good) {
            // Resources and slots are now part of resource_holder, which is done in "hp".  
            // When loading all, "hp" is done before "spells", so this will get it in that first time before it's saved in the new format.
            this.resource_holder.resources = [];
            if (piece && piece.resources && piece.resources.length > 0) {
              for (let i = 0; i < piece.resources.length; i++) {
                const char_resource = new CharacterResource(piece.resources[i]);
                this.resource_holder.resources.push(char_resource);
              }
            }
            this.resource_holder.slots = [];
            if (piece && piece.slots && piece.slots.length > 0) {
              for (let i = 0; i < piece.slots.length; i++) {
                const char_slot = new CharacterSlot(piece.slots[i]);
                this.resource_holder.slots.push(char_slot);
              }
            }
            get_special_resources = true;
          }
          this.spells = [];
          if (piece && piece.spells && piece.spells.length > 0) {
            // console.log(piece.spells);
            for (let i = 0; i < piece.spells.length; i++) {
              const char_spell = new CharacterSpell(piece.spells[i]);
              this.spells.push(char_spell);
            }
          }
          this.abilities = [];
          if (piece && piece.abilities && piece.abilities.length > 0) {
            for (let i = 0; i < piece.abilities.length; i++) {
              const ability_piece = piece.abilities[i];
              if (get_special_resources && ability_piece && ability_piece.special_resource_used && ability_piece.special_resource_used > 0) {
                this.resource_holder.special_resources.push({ ability_true_id: ability_piece.true_id, used: ability_piece.special_resource_used });
              }
              const char_ability = new CharacterAbility(ability_piece);
              this.abilities.push(char_ability);
            }
          }
          this.spell_as_abilities = [];
          if (piece && piece.spell_as_abilities && piece.spell_as_abilities.length > 0) {
            for (let i = 0; i < piece.spell_as_abilities.length; i++) {
              const ability_piece = piece.spell_as_abilities[i];
              if (get_special_resources && ability_piece && ability_piece.special_resource_used && ability_piece.special_resource_used > 0) {
                this.resource_holder.special_resources.push({ ability_true_id: ability_piece.true_id, used: ability_piece.special_resource_used });
              }
              const char_ability = new CharacterAbility(ability_piece);
              this.spell_as_abilities.push(char_ability);
            }
          }
          this.extra_effects = [];
          if (piece && piece.extra_effects && piece.extra_effects.length > 0) {
            for (let i = 0; i < piece.extra_effects.length; i++) {
              const ability_piece = piece.extra_effects[i];
              if (get_special_resources && ability_piece && ability_piece.special_resource_used && ability_piece.special_resource_used > 0) {
                this.resource_holder.special_resources.push({ ability_true_id: ability_piece.true_id, used: ability_piece.special_resource_used });
              }
              const char_ee = new CharacterExtraEffects(ability_piece);
              this.extra_effects.push(char_ee);
            }
          }
        break;
        case "items": 
          this.items = [];
          // debugger;
          // console.log(piece.items);
          if (piece && piece.items && piece.items.length > 0) {
            const types: string[] = [];
            for (let i = 0; i < piece.items.length; i++) {
              const item = piece.items[i];
              if (!types.includes(item.item_type)) {
                types.push(item.item_type);
              }
              // if (item.item_type === "Gem") {
              //   console.log(item);
              // }
              let char_item: CharacterItem | null = null;
              switch (item.item_type) {
                case "Armor":
                  char_item = new CharacterArmor(item);
                break;
                case "Weapon":
                  char_item = new CharacterWeapon(item);
                break;
                case "Ammunition":
                  char_item = new CharacterAmmunition(item);
                break;
                case "Bag":
                  char_item = new CharacterContainer(item);
                  this.containers.push(char_item as CharacterContainer);
                break;
                case "Container":
                  char_item = new CharacterContainer(item);
                  this.containers.push(char_item as CharacterContainer);
                break;
                case "Deck":
                  char_item = new CharacterDeck(item);
                break;
                case "Gem":
                  char_item = new CharacterGem(item);
                break;
                case "Book":
                  char_item = new CharacterBook(item);
                break;
                case "Custom":
                  if (item.true_id === "Equipment") {
                    if (this.containers.filter(o => o.true_id === "Equipment").length === 0) {
                      char_item = new CharacterContainer(item);
                      this.containers.push(char_item as CharacterContainer);
                    }
                  } else if (item.true_id === "Storage") {
                    if (this.containers.filter(o => o.true_id === "Storage").length === 0) {
                      char_item = new CharacterContainer(item);
                      this.containers.push(char_item as CharacterContainer);
                    }
                  } else {
                    char_item = new CharacterCustomItem(item);
                  }
                break;
                default:
                  char_item = new CharacterBasicItem(item);
                break;
              }
              if (char_item) {
                if (char_item.source_id === "") {
                  return "item";
                }
                this.items.push(char_item);
              }
            }
            if (this.containers.filter(o => o.name === "Equipment").length === 0) {
              const equipment = new CharacterContainer();
              equipment.name = "Equipment";
              equipment.true_id = "Equipment";
              this.containers.push(equipment);
              this.items.push(equipment);
              const storage = new CharacterContainer();
              storage.name = "Storage";
              storage.true_id = "Storage";
              this.containers.push(storage);
              this.items.push(storage);
            }
          }
          this.equipped_items = [];
          if (piece && piece.equipped_items && piece.equipped_items.length > 0) {
            for (let i = 0; i < piece.equipped_items.length; i++) {
              // All equipped items are also in items, and should have the same ids
              // All that gets saved to the db is the id
              if (this.equipped_items.filter(o => o.true_id === piece.equipped_items[i]).length === 0) {
                const obj_finder = this.items.filter(o => o.true_id === piece.equipped_items[i]);
                if (obj_finder.length === 1) {
                  this.equipped_items.push(obj_finder[0]);
                }
              }
            }
          }
          this.attuned_items = [];
          if (piece && piece.attuned_items && piece.attuned_items.length > 0) {
            for (let i = 0; i < piece.attuned_items.length; i++) {
              // All equipped items are also in items, and should have the same ids
              // All that gets saved to the db is the id
              if (this.attuned_items.filter(o => o.true_id === piece.equipped_items[i]).length === 0) {
                const obj_finder = this.items.filter(o => o.true_id === piece.attuned_items[i]);
                if (obj_finder.length === 1) {
                  this.attuned_items.push(obj_finder[0]);
                }
              }
            }
          }
          this.max_attuned_items = piece ? piece.max_attuned_items : 3;
          // console.log(piece);
          this.money = piece && piece.money ? new Money(piece.money) : new Money();
          this.other_possessions = piece ? piece.other_possessions : "";
          this.equipment_container.content_weight = -1;
          this.storage_container.content_weight = -1;
          const container_ids = this.items.filter(o => o.item_type === "Container").map(o => o.true_id);
          this.items = this.items.filter(o => 
            container_ids.includes(o.true_id) || 
            container_ids.includes(o.in_char_container_id));
          this.recipes_known = piece && piece.recipes_known ? piece.recipes_known.map((o: any) => new CharacterRecipe(o)) : [];
          this.recipe_components = piece && piece.recipe_components ? piece.recipe_components.map((o: any) => new CharacterRecipeComponent(o)) : [];
        break;
        case "race": 
          console.log("Race disappearing sometimes. Aasimar, Uriel.");
          console.log(piece);
          this.race = piece && piece.race ? new CharacterRace(piece.race) : new CharacterRace();
          if (this.race.source_id === "") {
            return "race";
          }
          this.standard_features = piece && piece.standard_features ? new CharacterStandardFeatures(piece.standard_features) : new CharacterStandardFeatures();
          if (this.standard_features.source_id === "") {
            return "standard_features";
          }
        break;
        case "gifts": 
          // debugger;
          this.gifts = [];
          if (piece && piece.gifts && piece.gifts.length > 0) {
            for (let i = 0; i < piece.gifts.length; i++) {
              const cg = new CharacterGift(piece.gifts[i]);
              if (cg.source_id === "") {
                return "gift";
              }
              this.gifts.push(cg);
            }
          }
        break;
        case "bastion_facilities": 
          // debugger;
          this.bastion_facilities = [];
          if (piece && piece.bastion_facilities && piece.bastion_facilities.length > 0) {
            for (let i = 0; i < piece.bastion_facilities.length; i++) {
              const cbf = new CharacterBastionFacility(piece.bastion_facilities[i]);
              if (cbf.source_id === "") {
                return "bastion_facility";
              }
              this.bastion_facilities.push(cbf);
            }
          }
        break;
        case "classes": 
          this.classes = [];
          if (piece && piece.classes && piece.classes.length > 0) {
            // console.log(piece.classes[0]);
            const char_classes: CharacterClass[] = [];
            for (let i = 0; i < piece.classes.length; i++) {
              const cc = new CharacterClass(piece.classes[i]);
              if (cc.source_id === "") {
                return "game_class";
              }
              char_classes.push(cc);
            }
            this.classes = char_classes.sort((a, b) => (a.position > b.position ? 1 : -1));
          }
        break;
        // case "orgs": 
        //   this.orgs = [];
        //   if (piece && piece.orgs && piece.orgs.length > 0) {
        //     for (let i = 0; i < piece.orgs.length; i++) {
        //       this.orgs.push(new CharacterOrg(piece.orgs[i]));
        //     }
        //   }
        // break;
        case "background": 
          this.background = piece && piece.background ? new CharacterBackground(piece.background) : new CharacterBackground();
          if (this.background.source_id === "") {
            return "background";
          }
        break;
      }
      // if (piece_name === "race") {
      //   console.log(this);
      // }
    }
    return "";
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      sidekick: this.sidekick,
      sidekick_creature_id: this.sidekick_creature_id,
      shared_id: this.shared_id,
      last_saved: this.last_saved,
      cookie_saved: this.cookie_saved,
      turn_resources: this.turn_resources,
      campaign_id: this.old_campaign_id,
      // active: this.active,
      knowledge: this.knowledge,
      character_options: this.character_options.toDBObj(),
      level_0_feature_ids: this.level_0_feature_ids,
      bonus_skill_profs_ids: this.bonus_skill_profs_ids,
      optional_feature_base_ids: this.optional_feature_base_ids,
      image_url: this.image_url,
      bg_image_url: this.bg_image_url,
      organizations: this.organizations,
      allies: this.allies,
      enemies: this.enemies,
      backstory: this.backstory,
      other_notes: this.other_notes,
      characteristics: this.characteristics,
      appearance: this.appearance,
      inspiration: this.inspiration,
      default_view: this.default_view,
      bar3_mode: this.bar3_mode,
      base_size: this.base_size,
      size_modifier: this.size_modifier,
      overriding_size: this.overriding_size,
      speed_customizations: this.speed_customizations,
      sense_customizations: this.sense_customizations,
      saving_throw_customizations: this.saving_throw_customizations,
      skill_customizations: this.skill_customizations,
      max_ability_score_customizations: this.max_ability_score_customizations,
      ability_score_customizations: this.ability_score_customizations,
      display_speed: this.display_speed,
      AC_customization: this.AC_customization,
      non_skill_prof_customizations: this.non_skill_prof_customizations,
      weapon_mastery_customizations: this.weapon_mastery_customizations.map(o => o.toDBObj()),
      last_campaign_update: this.last_campaign_update,
      unarmed_strike_customizations: this.unarmed_strike_customizations,
      attunement_customization: this.attunement_customization?.toDBObj()??null,
      saved_race_and_class_string: this.race_and_class_string,
      saved_race_string: this.race_string,
      saved_extras_string: this.extras_string,
      saved_race_ids: this.race_ids,
      saved_subrace_ids: this.subrace_ids,
      saved_class_info: this.class_info,
      saved_armor_class: this.use_ac,
      colors_for: this.colors_for,
      notes: this.notes,
    };
  }

  toCharacterDBObj = (piece: string) => {
    let obj: any = {};
    switch (piece) {
      case "abilities": 
        obj = {
          char_id: this._id,
          ability_score_gen_method: this.ability_score_gen_method,
          base_ability_scores: this.base_ability_scores.toDBObj(),
        };
      break;
      case "features": 
        const concentrating_on = this.concentrating_on ? this.concentrating_on.toDBObj() : null;
        if (concentrating_on) {
          concentrating_on._name = this.concentrating_on?.name??"";
        }
        obj = {
          char_id: this._id,
          extra_feats: this.extra_feats.map(o => o.toDBObj()),
          extra_damage_multipliers: this.extra_damage_multipliers.map(o => o.toDBObj()),
          favorite_conditions: this.favorite_conditions,
          conditions: this.conditions.map(o => o.toDBObj()),
          condition_immunities: this.condition_immunities,
          advantage: this.advantage,
          concentrating_on,
          minions: this.minions.map(o => o.toDBObj()),
          transform: this.transform ? this.transform.toDBObj() : null,
          use_transform: this.use_transform,
          // adaptations: this.adaptations.map(o => o.toDBObj()),
        };
      break;
      case "hp": // It being called "hp" is because I used to have it only have hp.  It should be called "resources" now, but I'm going to leave it.
        obj = {
          char_id: this._id,
          resource_holder: this.resource_holder.toDBObj(),
        };
      break;
      case "spells":
        obj = {
          char_id: this._id,
          spells: this.spells.map(o => o.toDBObj()),
          abilities: this.abilities.map(o => o.toDBObj()),
          spell_as_abilities: this.spell_as_abilities.map(o => o.toDBObj()),
          extra_effects: this.extra_effects.map(o => o.toDBObj())
        };
      break;
      case "items": 
        obj = {
          char_id: this._id,
          items: this.items.map(o => o.toDBObj()),
          equipped_items: this.equipped_items.map(o => o.true_id),
          attuned_items: this.attuned_items.map(o => o.true_id),
          max_attuned_items: this.max_attuned_items,
          other_possessions: this.other_possessions,
          money: this.money.toDBObj(),
          recipes_known: this.recipes_known.map(o => o.toDBObj()),
          recipe_components: this.recipe_components.map(o => o.toDBObj()),
        };
      break;
      case "race": 
        if (this.standard_features.char_feature_bases.length === 0) {
          // I've sometimes seen the languages get unselected, but it was while I was making code changes, so it might be fine.
          console.log(this.standard_features);
          console.log(this.standard_features.toDBObj());
        }
        obj = {
          char_id: this._id,
          race: this.race.toDBObj(),
          standard_features: this.standard_features.toDBObj(),
        };
      break;
      case "gifts": 
        obj = {
          char_id: this._id,
          gifts: this.gifts.map(o => o.toDBObj())
        };
      break;
      case "bastion_facilities": 
        obj = {
          char_id: this._id,
          bastion_facilities: this.bastion_facilities.map(o => o.toDBObj())
        };
      break;
      case "classes": 
        obj = {
          char_id: this._id,
          classes: this.classes.map(o => o.toDBObj())
        };
      break;
      case "background": 
        obj = {
          char_id: this._id,
          background: this.background.toDBObj()
        };
      break;
      case "orgs": 
        const orgs: any[] = [];
        for (let i = 0; i < this.orgs.length; i++) {
          orgs.push(this.orgs[i].toDBObj());
        }
        obj = {
          char_id: this._id,
          orgs
        };
      break;
    }
    return obj;
  }

  hit_dice_string(): string {
    let response = "";
    for (let i = 0; i < this.hit_dice.length; i++) {
      const hd = this.hit_dice[i];
      if (response !== "") {
        response += ", ";
      }
      response += `${hd.count}d${hd.size}`;
    }
    return response;
  }

  clone(drop_ids = false): Character {
    const clone = new Character();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: Character, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    
    this.shared_id = copyMe.shared_id;
    this.last_saved = copyMe.last_saved;
    this.cookie_saved = copyMe.cookie_saved;
    this.turn_resources = copyMe.turn_resources;

    this.connected = copyMe.connected;
    this.old_pieces = copyMe.old_pieces;
    this.owner_id = copyMe.owner_id;
    this.sidekick = copyMe.sidekick;
    this.sidekick_creature_id = copyMe.sidekick_creature_id;
    this.sidekick_creature = copyMe.sidekick_creature;
    this.is_favorite = copyMe.is_favorite;
    this.is_archived = copyMe.is_archived;
    
    this.knowledge = copyMe.knowledge.map(o => { return {...o}; });
    // this.adaptations = copyMe.adaptations.map(o => o.clone());

    this.character_options = copyMe.character_options.clone();
    this.level_0_feature_ids = [...copyMe.level_0_feature_ids];
    this.bonus_skill_profs_ids = [...copyMe.bonus_skill_profs_ids];

    this.old_campaign_id = copyMe.old_campaign_id;
    // this.active = copyMe.active;

    this.image_url = copyMe.image_url;
    this.bg_image_url = copyMe.bg_image_url;

    this.race = copyMe.race.clone();
    this.size = copyMe.size; 
    this.base_size = copyMe.base_size;
    this.size_modifier = copyMe.size_modifier;
    this.overriding_size = copyMe.overriding_size;
    this.size_bonus_for_carrying = copyMe.size_bonus_for_carrying;
    this.carrying_capacity_mult = copyMe.carrying_capacity_mult;
    this.max_carrying_capacity = copyMe.max_carrying_capacity;
    this.gifts = copyMe.gifts.map(o => o.clone());
    this.bastion_facilities = copyMe.bastion_facilities.map(o => o.clone());
    this.classes = copyMe.classes.map(o => o.clone());
    this.background = copyMe.background.clone();
    this.orgs = copyMe.orgs.map(o => o.clone());
    this.standard_features = copyMe.standard_features.clone();

    this.ability_score_gen_method = copyMe.ability_score_gen_method;
    this.base_ability_scores = copyMe.base_ability_scores.clone();
    this.current_ability_scores = copyMe.current_ability_scores.clone();
    this.bonus_ability_score_modifiers = copyMe.bonus_ability_score_modifiers.clone();
    this.override_ability_scores = copyMe.override_ability_scores.clone();
    this.max_ability_scores = copyMe.max_ability_scores.clone();

    // this.extra_skill_bonuses = {...copyMe.extra_skill_bonuses};
    // this.skill_overrides = {...copyMe.skill_overrides};
    // this.proficiency_overrides = {...copyMe.proficiency_overrides};

    this.organizations = copyMe.organizations;
    this.allies = copyMe.allies;
    this.enemies = copyMe.enemies;
    this.backstory = copyMe.backstory;
    this.other_notes = copyMe.other_notes;

    this.characteristics = {...copyMe.characteristics};
    this.appearance = copyMe.appearance;

    this.items = copyMe.items.map(o => o.clone());
    this.equipped_items = copyMe.equipped_items.map(o => o.clone());
    this.attuned_items = copyMe.attuned_items.map(o => o.clone());
    this.containers = copyMe.containers.map(o => o.clone());
    this.recipes_known = copyMe.recipes_known.map(o => o.clone());
    this.recipe_components = copyMe.recipe_components.map(o => o.clone());

    this.max_attuned_items = copyMe.max_attuned_items;
    this.money = copyMe.money.clone();
    this.other_possessions = copyMe.other_possessions;
    this.default_view = copyMe.default_view;
    this.bar3_mode = copyMe.bar3_mode;

    // These are feats granted by the DM, not gained through ASI or race choices
    this.extra_feats = copyMe.extra_feats.map(o => o.clone());
    // These are damage multipliers added by spell effects, etc
    this.extra_damage_multipliers = copyMe.extra_damage_multipliers.map(o => o.clone());
    this.favorite_conditions = [...copyMe.favorite_conditions];
    // this.condition_hash = {...copyMe.condition_hash};
    this.conditions = copyMe.conditions.map(o => o.clone());
    this.condition_immunities = [...copyMe.condition_immunities];

    this.advantage = copyMe.advantage;
    this.inspiration = copyMe.inspiration;

    this.resource_holder = copyMe.resource_holder.clone();
    
    this.speed_customizations = {...copyMe.speed_customizations};
    this.sense_customizations = {...copyMe.sense_customizations};
    this.saving_throw_customizations = {...copyMe.saving_throw_customizations};
    this.skill_customizations = {...copyMe.skill_customizations};
    this.max_ability_score_customizations = {...copyMe.max_ability_score_customizations};
    this.ability_score_customizations = {...copyMe.ability_score_customizations};

    this.display_speed = copyMe.display_speed;
    this.AC_customization = {...copyMe.AC_customization};
    this.non_skill_prof_customizations = {...copyMe.non_skill_prof_customizations};
    this.weapon_mastery_customizations = copyMe.weapon_mastery_customizations.map(o => o.clone());

    // this.death_save_fails = copyMe.death_save_fails;
    // this.death_save_successes = copyMe.death_save_successes;

    this.ability_features = copyMe.ability_features.map(o => o.clone());
    this.characteristic_features = copyMe.characteristic_features.map(o => o.clone());
    this.characteristic_feature_source_names = [...copyMe.characteristic_feature_source_names];
    this.spells = copyMe.spells.map(o => o.clone());
    this.ritual_only = copyMe.ritual_only.map(o => o.clone());
    // this.special_spells = copyMe.special_spells.map(o => o.clone());
    this.concentrating_on = copyMe.concentrating_on?.clone()??null;
    this.abilities = copyMe.abilities.map(o => o.clone());
    this.spell_as_abilities = copyMe.spell_as_abilities.map(o => o.clone());

    this.minions = copyMe.minions.map(o => o.clone());
    this.transform = copyMe.transform?.clone()??null;
    this.use_transform = copyMe.use_transform;
    if (this.transform) {
      this.transform.character = this;
    }
    // this.transforms = copyMe.transforms.map(o => o.clone());

    this.optional_feature_base_ids = [...copyMe.optional_feature_base_ids];
    
    // Partially Calculated
    this.resource_features = copyMe.resource_features.map(o => o.clone());
    // Calculated attributes
    this.char_feature_bases = copyMe.char_feature_bases.map(o => o.clone());

    this.asi_features = copyMe.asi_features.map(o => o.clone());
    this.ability_score_features = copyMe.ability_score_features.map(o => {
      return { cf: o.cf.clone(), casibf: o.casibf.clone() };
    });
    // this.max_hit_points = copyMe.max_hit_points;

    this.language_features = copyMe.language_features.map(o => o.clone());
    this.languages_known = [...copyMe.languages_known];
    
    this.proficiency_features = copyMe.proficiency_features.map(o => o.clone());
    this.proficiencies = copyMe.proficiencies.map(o => { return {...o}; });
    this.jack_of_all_trades = copyMe.jack_of_all_trades;

    this.modifier_features = copyMe.modifier_features.map(o => o.clone());
    this.spell_modifier_features = copyMe.spell_modifier_features.map(o => o.clone());
    this.summon_upgrade_features = [...copyMe.summon_upgrade_features]; // Not cloned because these are the Floating Features, not the Char Features.  I probably should change that
    this.attack_upgrades = copyMe.attack_upgrades.map(o => o.clone());
    this.weapon_damage_upgrades = copyMe.weapon_damage_upgrades.map(o => o.clone());
    this.extra_effects = copyMe.extra_effects.map(o => o.clone());
    // this.bonus_action_attacks = copyMe.bonus_action_attacks.map(o => o.clone());
    this.light_attack_add_damage_mods = copyMe.light_attack_add_damage_mods.map(o => o.clone());
    this.table_modifiers = [...copyMe.table_modifiers]; // same
    this.spell_modifiers = copyMe.spell_modifiers.map(o => o.clone());
    this.passive_bonuses = {...copyMe.passive_bonuses};
    this.range_bonuses = copyMe.range_bonuses.map(o => { return {...o}; });

    this.rerolls = copyMe.rerolls.map(o => o.clone());
    this.minimum_rolls = copyMe.minimum_rolls.map(o => o.clone());

    this.source_features = copyMe.source_features.map(o => o.clone());
    this.conditions_from_features = copyMe.conditions_from_features.map(o => o.clone());
    this.feats = copyMe.feats.map(o => o.clone());
    this.eldritch_invocations = copyMe.eldritch_invocations.map(o => o.clone());
    this.fighting_styles = copyMe.fighting_styles.map(o => o.clone());
    this.pact_boon = copyMe.pact_boon ? copyMe.pact_boon.clone() : null;
  
    this.special_features = copyMe.special_features.map(o => o.clone());
  
    this.speed = {
      walk: copyMe.speed.walk,
      swim: copyMe.speed.swim,
      climb: copyMe.speed.climb,
      fly: copyMe.speed.fly,
      burrow: copyMe.speed.burrow
    };

    this.attack_bonuses = copyMe.attack_bonuses.map(o => o.clone());
    this.damage_bonuses = copyMe.damage_bonuses.map(o => o.clone());
    this.saving_throw_bonuses = copyMe.saving_throw_bonuses.map(o => o.clone());
    this.check_bonuses = copyMe.check_bonuses.map(o => o.clone());
    this.initiative_modifier = copyMe.initiative_modifier;
    this.initiative_bonuses = copyMe.initiative_bonuses.map(o => o.clone());
    this.base_armor_class = copyMe.base_armor_class;
    this.armor_class = copyMe.armor_class;
    this.max_ac_dex_bonus_for_med_armor = copyMe.max_ac_dex_bonus_for_med_armor;
    this.saving_throw_profs = {...copyMe.saving_throw_profs};

    this.ability_checks = { 
      STR: copyMe.ability_checks.STR.clone(), 
      DEX: copyMe.ability_checks.DEX.clone(),
      CON: copyMe.ability_checks.CON.clone(),
      INT: copyMe.ability_checks.INT.clone(),
      WIS: copyMe.ability_checks.WIS.clone(),
      CHA: copyMe.ability_checks.CHA.clone(),
    };
    this.initiative_check = copyMe.initiative_check.clone();
    this.saving_throws = { 
      STR: copyMe.saving_throws.STR.clone(), 
      DEX: copyMe.saving_throws.DEX.clone(),
      CON: copyMe.saving_throws.CON.clone(),
      INT: copyMe.saving_throws.INT.clone(),
      WIS: copyMe.saving_throws.WIS.clone(),
      CHA: copyMe.saving_throws.CHA.clone(),
    };

    this.skill_checks = copyMe.skill_checks.map(o => { return { skill_id: o.skill_id, skill_check: o.skill_check.clone() }});

    // this.armor_worn = copyMe.armor_worn;
    // this.shield_carried = copyMe.shield_carried;

    this.sense_features = copyMe.sense_features.map(o => o.clone());
    this.senses = copyMe.senses.map(o => o.clone());

    this.start_equipment_features = copyMe.start_equipment_features.map(o => o.clone());
    this.misc_features = copyMe.misc_features.map(o => o.clone());
    this.advantages = [...copyMe.advantages];
    this.damage_multipliers = [...copyMe.damage_multipliers];
    this.extra_attacks = copyMe.extra_attacks;
    this.unarmed_strike_scores = [...copyMe.unarmed_strike_scores];
    this.unarmed_strike_bonus_action = copyMe.unarmed_strike_bonus_action;
    this.unarmed_strike_damage_types = [...copyMe.unarmed_strike_damage_types];
    this.unarmed_strike_size = copyMe.unarmed_strike_size;
    this.unarmed_strike_count = copyMe.unarmed_strike_count;
    this.unarmed_strike_add_attack_to_dmg = copyMe.unarmed_strike_add_attack_to_dmg;
    this.show_extra_conditions = copyMe.show_extra_conditions;
    this.spellcasting_features = copyMe.spellcasting_features.map(o => o.clone());
    this.actions = [...copyMe.actions];
    this.full_actions = [...copyMe.full_actions];
    this.bonus_actions = [...copyMe.bonus_actions];
    this.reactions = [...copyMe.reactions];
    this.action_type_upgrades = copyMe.action_type_upgrades.map(o => o.clone());
    this.merged_special_feature_types = [];
    for (const msft of copyMe.merged_special_feature_types) {
      this.merged_special_feature_types.push([...msft]);
    }
    this.formula_values = {...copyMe.formula_values};
    this.encumbrance_level = copyMe.encumbrance_level;
    this.special_feat_accesses = [...copyMe.special_feat_accesses];

    this.last_campaign_update = copyMe.last_campaign_update;

    this.unarmed_strike_customizations = {...copyMe.unarmed_strike_customizations};
    this.attunement_customization = copyMe.attunement_customization?.clone()??null;
    
    this.colors_for = {...copyMe.colors_for};

    this.receivables = copyMe.receivables.map(o => o.clone());

    this.dragonmarked_source_id = copyMe.dragonmarked_source_id;
    
    this.saved_race_and_class_string = copyMe.saved_race_and_class_string;

    this.notes = copyMe.notes;
  }

  get race_and_class_string(): string {
    const race_string = this.race_string;
    const class_string = this.class_string;
    if (["No Race","No Species"].includes(race_string) && class_string === "No Class") {
      return this.saved_race_and_class_string;
    }
    return `${race_string} ${class_string}`;
  }

  get race_string(): string {
    let str = "No Race";
    if (this.sidekick && this.sidekick_creature) {
      str = this.sidekick_creature.use_name;
    } else {
      str = this.race.use_name;
    }
    if (str === "No Race") {
      str = this.saved_race_string;
    }
    if (this.character_options.races_called === "Species" && str.includes("No Race")) {
      str = str.replace("No Race", "No Species");
    }
    return str;
  }

  get extras_string() {
    if (`${this.race.use_name} ${this.class_string}` === "No Race No Class" || `${this.race.use_name} ${this.class_string}` === "No Species No Class") {
      return this.saved_extras_string;
    } else {
      return `${this.sidekick ? " (Sidekick)" : ""}${this.owner_id === "" ? " - Unclaimed" : ""}`;
    }
  }

  get class_string(): string {
    // if (this.name.includes("Marius")) {
    //   debugger;
    // }
    if (this.classes.length === 0) {
      return this.saved_class_info.length === 0 ? "No Class" : this.saved_class_info.map(o => `${ o.class_name } ${ o.level }`).join(", ");
    } else if (this.classes.length === 1) {
      const char_class = this.classes[0];
      return `${ char_class.get_use_name() } ${ char_class.level }`;
    } else {
      return this.classes.sort((a, b) => (a.position > b.position ? 1 : -1)).map(char_class => {
        return `${ char_class.source?.name??"" } ${ char_class.level }`;
      }).join("/");
    }
  }

  get weight_carried(): number {
    let weight = 0;

    for (const container of this.containers.filter(o => 
      (o.true_id === "Equipment" || o.equipped) && 
      o.true_id !== "Storage")) {
      weight += container.weight;
      if (!container.ignore_weight) {
        weight += container.content_weight;
      }
    }

    return weight;
  }

  get equipment_container(): CharacterContainer {
    const equipment_finder = this.containers.filter(o => o.true_id === "Equipment");
    if (equipment_finder.length === 1) {
      return equipment_finder[0];
    } else {
      const equipment = new CharacterContainer();
      equipment.name = "Equipment";
      equipment.true_id = "Equipment";
      this.containers.push(equipment);
      this.items.push(equipment);
      return equipment;
    }
  }

  get storage_container(): CharacterContainer {
    const storage_finder = this.containers.filter(o => o.true_id === "Storage");
    if (storage_finder.length === 1) {
      return storage_finder[0];
    } else {
      const storage = new CharacterContainer();
      storage.name = "Storage";
      storage.true_id = "Storage";
      this.containers.push(storage);
      this.items.push(storage);
      return storage;
    }
  }

  get skill_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Skill");
  }

  get tool_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Tool");
  }

  get hp() {
    return this.resource_holder.hp;
  }

  get hit_dice() {
    return this.resource_holder.hit_dice;
  }

  get true_max_hit_points() {
    return this.hp.true_max;
  }

  get special_proficiencies() {
    return this.proficiencies.filter(o => o.type.includes("Special"));
  }

  get weapon_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Weapon");
  }

  get armor_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Armor");
  }

  get saving_throw_proficiencies() {
    return this.proficiencies.filter(o => o.type === "Saving Throw");
  }

  get expertises() {
    return this.proficiencies.filter(o => o.type === "Expertise");
  }

  get weapon_keyword_upgrades(): WeaponKeywordUpgradeFeature[] {
    return this.attack_upgrades.filter(o => o.floating_feature instanceof WeaponKeywordUpgradeFeature).map(o => o.floating_feature) as WeaponKeywordUpgradeFeature[];
  }

  get weapon_keyword_upgrade2s(): WeaponKeywordUpgradeFeature2[] {
    return this.attack_upgrades.filter(o => o.floating_feature instanceof WeaponKeywordUpgradeFeature2).map(o => o.floating_feature) as WeaponKeywordUpgradeFeature2[];
  }

  get all_char_feature_sources(): CharacterFeatureSource[] {
    let all_char_feature_sources: CharacterFeatureSource[] = [];
    all_char_feature_sources = [this.race,this.background,...this.classes,...this.gifts,...this.conditions,...this.extra_feats,...this.items,...this.orgs,this.standard_features];
    let i = 0;
    while (i < all_char_feature_sources.length) {
      const cfs = all_char_feature_sources[i];
      all_char_feature_sources = [...all_char_feature_sources,...cfs.sub_char_feature_sources];
      ++i;
    }
    return all_char_feature_sources;
  }

  get fs_tags() {
    return this.all_char_feature_sources.flatMap(o => o.tags);
  }
  getFSTagsForSourceTypes(source_types: string[]) {
    return this.all_char_feature_sources.filter(o => 
      source_types.includes(o.feature_source_type)
    ).flatMap(o => o.tags);
  }

  get spellbook_spells(): CharacterSpell[] {
    return this.classes.flatMap(o => o.spellbook?.spells??[]);
  }
}

export interface Customization {
  [ability_score: string]: { 
    [type: string]: { // Override, Modifier, Proficiency Override
      value: number | null, 
      notes: string 
    }
  },
}

export class CharacterRoll {
  modifier = 0;
  proficiency = 0;
  rolls: RollPlus[] = [];
  advantage = 0;
  advantages: CharacterAdvantage[] = [];

  constructor(obj?: any) {
    if (obj) {
      this.modifier = obj.modifier ? obj.modifier : 0;
      this.proficiency = obj.proficiency ? obj.proficiency : 0;
      this.rolls = obj.rolls ? obj.rolls.map((o: any) => new RollPlus(o)) : [];
      this.advantage = obj.advantage ? obj.advantage : 0;
      this.advantages = obj.advantages ? obj.advantages : [];
    }
  }

  toDBObj() {
    return {
      modifier: this.modifier,
      proficiency: this.proficiency,
      rolls: this.rolls.map(o => o.toDBObj()),
      advantage: this.advantage,
      advantages: this.advantages
    }
  }

  copy(copyMe: CharacterRoll) {
    this.modifier = copyMe.modifier;
    this.proficiency = copyMe.proficiency;
    this.rolls = copyMe.rolls.map(o => o.clone());
    this.advantage = copyMe.advantage;
    this.advantages = [...copyMe.advantages];
  }

  clone() {
    const new_obj = new CharacterRoll();
    new_obj.copy(this);
    return new_obj;
  }
} 

export interface RangeBonus {
  attack_type: AttackType, 
  attack_details: string[], // ...Weapon Keywords, Unarmed Strike, ...Spell Schools, ...Damage Types
  damage_types: (DamageType | "Any")[],
  mod_type: ModType,
  amount: number,
  char_feature: CharacterFeature | Feature
}